import type { SupportedMcpOAuthEnvironment } from './mcp-oauth-environment';
import { buildClaudeCodeCommand } from './mcp-oauth-environment';

export interface McpConnectorAccount {
  id: string;
  name: string;
}

export interface AccountScopedMcpConnectorConfig {
  claudeCodeCommand: string;
  connectorName: string;
  mcpUrl: string;
}

/**
 * Query param is ignored by the Savvy MCP server — OAuth determines the account.
 * It exists so Claude treats each account's URL as a distinct custom connector.
 */
export const buildAccountScopedMcpUrl = (
  baseMcpUrl: string,
  accountId: string,
): string => {
  const url = new URL(baseMcpUrl);
  url.searchParams.set('accountId', accountId);

  return url.toString();
};

export const slugifyAccountNameForMcpAlias = (accountName: string): string =>
  accountName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);

export const buildClaudeCodeServerAlias = (params: {
  accountId: string;
  accountName: string;
}): string => {
  const slug = slugifyAccountNameForMcpAlias(params.accountName);

  if (slug) {
    return `savvy-${slug}`;
  }

  return `savvy-${params.accountId.slice(0, 8)}`;
};

export const buildAccountScopedMcpConnectorConfig = (params: {
  account: McpConnectorAccount;
  connectorName: string;
  environment: SupportedMcpOAuthEnvironment;
}): AccountScopedMcpConnectorConfig => {
  const mcpUrl = buildAccountScopedMcpUrl(
    params.environment.mcpUrl,
    params.account.id,
  );
  const claudeCodeServerAlias = buildClaudeCodeServerAlias({
    accountId: params.account.id,
    accountName: params.account.name,
  });

  return {
    claudeCodeCommand: buildClaudeCodeCommand(mcpUrl, claudeCodeServerAlias),
    connectorName: params.connectorName,
    mcpUrl,
  };
};

export const buildFallbackMcpConnectorConfig = (
  environment: SupportedMcpOAuthEnvironment,
): AccountScopedMcpConnectorConfig => ({
  claudeCodeCommand: environment.claudeCodeCommand,
  connectorName: 'Savvy',
  mcpUrl: environment.mcpUrl,
});
